import { NgModule } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppUtilsModule } from './app-utils.module';
import { YeshivaEnums } from 'src/serverTypes/yeshivaTypesEnums';
import { YeshivaModels } from 'src/serverTypes/yeshivaTypesDeclare';

declare var Android: any;

function getBrowserWindow(): Window | undefined {
    return typeof window === 'undefined' ? undefined : window;
}

function getBrowserDocument(): Document | undefined {
    return typeof document === 'undefined' ? undefined : document;
}

@NgModule()
export class AppGlobalsModule {
    static projectPath = 'siteProjects/AngularApp';
    static readonly NumberOfLessonsInPage: number = 40;
    private static readonly DEFAULT_PICTURES_PATH: string = `/SiteProjects/YeshivaModels/Handlers/InternalFilesFake.jpg?act=pictures&f=`;

    //   /AngularApp   +   /Partial/Current
    static partialViewsPath(view: string): string {
        return '../Partial/' + view;
    }

    /**
     *  @deprecated - use webPack alias: cssFolder
     *      require('content/filter-tabs.component.scss')
     */
    //  ../siteProjects/AngularApp/content/current.component.scss
    static stylePath(file: string): string {
        return '../' + this.projectPath + '/content/' + file;
    }

    /**
     *  @deprecated - use webPack alias: htmlFolder
     *      require('views/AngularTemplates/filter-tabs.html'),
     */
    //   /siteProjects/AngularApp/Views/AngularTemplates/lesson-detail.html
    static htmlPath(file: string): string {
        return '/' + this.projectPath + '/Views/AngularTemplates/' + file;
    }
    static readonly NAVIGATIONS_SUM: string = 'navigationSum';

    // Is server is singleton
    private static _isServer = false;
    private static _singletonFlag = false;

    /**
     * Check if running environment is Angular Universal ( SSR )
     */
    static get isServer(): boolean {
        return this._isServer;
    }
    static set isServer(isServer: boolean) {
        if (!this._singletonFlag) {
            this._isServer = isServer;
            this._singletonFlag = true;
        }
    }

    static AndroidApp = !AppGlobalsModule.isServer && typeof Android !== 'undefined' && Android != null;

    private static get _window(): Window | undefined {
        return getBrowserWindow();
    }

    private static get _document(): Document | undefined {
        return getBrowserDocument();
    }

    /**
     * Check if environment is debugging
     */
    static get isDevelopment(): boolean {
        return !environment.production;
    }

    /**
     * True when running inside the CMS content editor iframe.
     * The CMS sets `window.isCms = true` in the editor iframe (see ContentEntityManagement.master).
     * On public pages the flag is undefined, so this returns false. SSR-safe via the isServer guard.
     */
    static get isCms(): boolean {
        if (this.isServer) {
            return false;
        }
        return (this._window as (Window & { isCms?: boolean }) | undefined)?.isCms === true;
    }

    static warehousePath(request: string, language: string): string {
        // In prod we need to support multi language
        if (environment.production) {
            return this.getClientDomain(language) + '/warehouse/' + request;
        }

        //FIXME: On remote, we need to have yeshiva.org.il as endpoint but coockies doesn't sent due to different domain.
        if (false) {
            return AppUtilsModule.createHost(environment.warehouse.protocol, environment.warehouse.domain, environment.warehouse.port) + '/warehouse/' + request;
        } else {
            // Use explicit protocol to avoid protocol-relative URLs which can leak XSRF tokens
            const location = this._window?.location;
            return (
                (location?.protocol ?? 'https:') +
                '//' +
                (location?.hostname ?? environment.warehouse.domain) +
                ':' +
                environment.warehouse.port +
                '/warehouse/' +
                request
            );
        }
    }

    // /api/lessonsAPI
    static apiPath(request: string, base: string = '', apiAddon: string = '/api/'): string {
        if (base !== '' && base[0] !== '/') {
            base = '/' + base;
        }

        if (this.isServer) {
            //the port includes : if not 80 for varnish
            return AppUtilsModule.createHost(environment.api.protocol, environment.api.domain, environment.api.port) + base + apiAddon + request;
        }
        return base + apiAddon + request;
    }
    /**
     * JS setTimeout without emitting on server since it blocks the rendering.
     * @param callback Function to emit
     * @param timeout Millis to emit afterward. Pass -1 to skip; pass 0 to schedule after the current call stack.
     * @returns Null if the function won't emit, otherwise return the timerId.
     */
    static setTimeoutIsNotServer(callback: () => void, timeout: number = 0): NodeJS.Timeout | null {
        return this.isServer || timeout === -1
            ? null
            : setTimeout(() => {
                  callback();
              }, timeout);
    }

    static scrollToIsNotServer(options?: ScrollToOptions): void {
        if (!this.isServer) {
            this._window?.scrollTo(options);
        }
    }

    /**
     * The function allows you to focus on a member in a scrolling list without scrolling to it
     *
     * @param elementUl the element of the list. nativeElement and its any... Allow finding an element by getElement
     * @param elementLi the element of the selected row in the list
     */
    static scrollListIsNotServer(elementUl: any, elementLi: any): void {
        if (!this.isServer) {
            elementUl.scrollTop = elementLi.offsetTop - elementUl.offsetTop;
        }
    }

    static userApiPath(request: string): string {
        // Switched to upper U so serv proxy will smoothly redirect to .NET
        return '/Users/' + request;
    }

    /**
     * A global function to get a link url to a media files
     *
     * @returns return a string of the media url from schema to the A-Z directory
     */
    static mediaPath(beitMidrashFolder: string = 'yeshiva'): string {
        return AppUtilsModule.createHost(environment.video.protocol, environment.video.domain, environment.video.port) + `/v/${beitMidrashFolder}/`;
    }

    static otherLanguageHost(currentLanguage: string): string {
        const protocol: string = (environment.production ? 'https' : 'http') + '://';
        const isCurrHe: boolean = currentLanguage === 'heb';
        const urlProd: string = isCurrHe ? 'www.yeshiva.co' : 'www.yeshiva.org.il';
        const urlDev: string = isCurrHe ? 'en.local' : 'he.local';
        return protocol + (environment.production ? urlProd : urlDev);
    }
    /**
     * A global function to get a link url to a pic files
     *
     * @returns return a string of the pic url server. add / if needed
     */
    static picPath(link: string = AppGlobalsModule.DEFAULT_PICTURES_PATH): string {
        let picPath = AppUtilsModule.createHost(environment.pic.protocol, environment.pic.domain, environment.pic.port);

        //if no link or link doesn't start with /
        if (!link || link === '' || link[0] !== '/') {
            //add / at domain end
            picPath += '/';
        }

        return picPath + link;
    }

    /**
     * A global function to get a link url to a assets files
     *
     * @returns return a string of the assets url
     */
    static assetsPath(lang: string = 'he', another = false): string {
        if (this.isServer) {
            let address = '';
            if (environment.production) {
                address += this.getClientDomain(lang);
            } else {
                address += AppUtilsModule.createHost(environment.assets.protocol, environment.assets.domain, environment.assets.port);
            }
            if (another) {
                return address + '/';
            }
            return address + '/' + this.projectPath + `/dist/client/${lang}/assets/`;
        } else {
            if (another) {
                return '/';
            }
            return '/' + this.projectPath + `/dist/client/${lang}/assets/`;
        }
    }

    static svgAssetsFullPath(lang: string = 'he'): string {
        return this.assetsPath(lang) + 'svg/';
        // We must initiate the same windows ro bypass CORS
        // return "/siteprojects/AngularApp/dist/" + lang + "/assets/svg/";
    }

    static imgNotAngularAssetsFullPath(lang: string = 'he'): string {
        return this.assetsPath(lang, true) + 'content/images/';
        // We must initiate the same windows ro bypass CORS
        // return "/siteprojects/AngularApp/dist/" + lang + "/assets/svg/";
    }

    /**
     * return the name of the curent content area.
     * if no content area recognize - return midrash
     */
    //TODO: use the activatedRoute from appStore
    static areaPath(): string {
        let area = this._document?.location.pathname.split('/')[1] ?? '';
        if (area === 'ask') {
            return 'ask';
        } else {
            return 'midrash';
        }
    }

    /**
     * A global function to get a link url to a category page
     *
     * @param catId The id of the category
     * @returns return a string of the category url
     */
    static categoryLink(catId: number): string {
        return '/' + this.areaPath() + '/category/' + catId;
    }

    /**
     * A global function to get a link url to a SERIES page (the series' latest lesson playing)
     *
     * @param seriesId The id of the series' category
     * @returns return a string of the series page url
     */
    static seriesLink(seriesId: number): string {
        return '/midrash/series/' + seriesId;
    }

    /**
     * Where a SERIES is linked from (its name, its card, 2026-10-23): the LESSON page of its
     * latest lesson - a lesson that belongs to a series shows the series page at the lesson's
     * own url (content-detail's seriesPageComponent), so no link points at /midrash/series/<id>
     * any more. The latest lesson is the highest id among the lessons at hand; with none, the
     * series route stays as the fallback.
     *
     * @param contents the series' lessons at hand (any page of it)
     * @param seriesId the series' category id (the fallback)
     */
    static seriesLessonLink(contents: { Id: number }[] | null | undefined, seriesId: number): string {
        let latest: { Id: number } | null = null;
        for (const lesson of contents ?? []) {
            if (lesson?.Id && (!latest || lesson.Id > latest.Id)) {
                latest = lesson;
            }
        }
        return latest ? this.lessonLink(latest.Id) : this.seriesLink(seriesId);
    }

    static categoryLinkbyType(
        catId: number,
        contentType: YeshivaEnums.YeshivaModels.BE.EntityType = YeshivaEnums.YeshivaModels.BE.EntityType.MIDRASH_CATEGORY,
    ): string {
        let url: string;

        switch (contentType) {
            case YeshivaEnums.YeshivaModels.BE.EntityType.MIDRASH_CATEGORY:
                url = '/midrash/';
                break;
            case YeshivaEnums.YeshivaModels.BE.EntityType.ASK_CATEGORY:
                url = '/ask/';
                break;
            default:
                url = '';
        }

        return url + 'category/' + catId;
    }

    /**
     * A global function to get a link url to a lesson page
     *
     * @param id The id of the lesson
     * @returns return a string of the lesson url
     */
    static lessonLink(id: number, contentType: YeshivaEnums.YeshivaModels.BE.EntityType = YeshivaEnums.YeshivaModels.BE.EntityType.SHIUR): string {
        let url: string;

        switch (contentType) {
            case YeshivaEnums.YeshivaModels.BE.EntityType.SHIUR:
                url = '/midrash/';
                break;
            case YeshivaEnums.YeshivaModels.BE.EntityType.QUESTION:
                url = '/ask/';
                break;
            default:
                url = '';
        }

        url += id;

        return url;
    }

    /**
     * A global function to get a link url to a lesson page
     *
     * @param id The id of the lesson
     * @returns return a string of the lesson url
     */
    static lessonLinkEntity(entity: YeshivaModels.BE.ContentEntity): string {
        if (!entity?.Id || !entity?.Type) {
            return '';
        }
        return this.lessonLink(entity.Id, entity.Type);
    }

    /**
     * A global function to get a link url to a category page
     *
     * @param rabbiId The id of the rabbi
     * @returns return a string of the rabbi page url
     */
    static rabbiLink(rabbiId: number): string {
        return `/rabbi/${rabbiId}`;
    }

    /**
     * A global function to get a link url to a category page
     *
     * @param tagId The id of the tag
     * @returns return a string of the tag page url
     */
    static tagLink(tagId: number): string {
        if (this.isServer) {
            return '/tags/' + tagId;
        }
        // Return absolute URL with explicit protocol to avoid protocol-relative URLs
        const location = this._window?.location;
        return location ? location.protocol + '//' + location.host + '/tags/' + tagId : '/tags/' + tagId;
    }

    /**
     * A global function to get a link url to a category page
     *
     * @param articleId The id of the article
     * @returns return a string of the tag page url
     */
    static articleLink(articleId: number): string {
        if (this.isServer) {
            return '/article/' + articleId;
        }
        // Return absolute URL with explicit protocol to avoid protocol-relative URLs
        const location = this._window?.location;
        return location ? location.protocol + '//' + location.host + '/article/' + articleId : '/article/' + articleId;
    }
    static getClientDomain(language: string): string {
        return language === 'heb' || language === 'he' ? 'https://www.yeshiva.org.il' : 'https://www.yeshiva.co';
    }

    /**
     * Normalize legacy spriteRszRound image URLs to the static rabanim/midrash paths to avoid hitting the ASPX handler.
     */
    static sanitizeRabbiImageUrl(imgUrl: string | undefined): string {
        if (!imgUrl) {
            return '';
        }

        let url = imgUrl;

        // Capture the whole cache-busting query (v, cacheVer, ...) once so it can be reattached
        // verbatim after the path is rewritten. This forwards every param rather than only v, so
        // cacheVer is preserved even when v is absent.
        const query = AppGlobalsModule.extractImageQuery(url);

        const rabbiMatch = /\/SiteProjects\/YeshivaModels\/Logic\/Images\/spriteRszRound\.aspx\?pic=R-([^&?]+)/i.exec(url);
        if (rabbiMatch) {
            return `/images/rabanim/${rabbiMatch[1]}${query}`;
        }

        if (/spriterszround\.aspx\?pic=v1-/i.test(url)) {
            let tempImg = url.replace(/.*spriterszround\.aspx\?pic=v1-/i, '');
            const id = /[0-9]+/.exec(tempImg)?.[0];

            if (id) {
                const picFolder = 'pic' + tempImg.substring(0, id.length - 2);
                tempImg = tempImg.replace(/[0-9]+-/, '');

                // Strip everything from the first query separator ('&' or '?'); the query was
                // already captured above and is reattached via `query`.
                tempImg = tempImg.replace(/[\\?&].*/, '');
                url = `/midrash/pic/${picFolder}/${tempImg}.jpg${query}`;
            }
        }

        return url;
    }

    /**
     * Pull the cache-busting query (v, cacheVer, ...) off a legacy spriteRszRound URL so it can be
     * reattached after the path is rewritten. The params follow the pic= value, separated by '&'
     * (e.g. "...pic=R-x.jpg&v=123&cacheVer=456"). Returns the params with a leading '?', or '' when
     * there are none, so callers never append an empty/malformed query.
     */
    private static extractImageQuery(url: string): string {
        // Match the pic= value, then capture everything after its first '&'/'?' separator.
        const match = /[?&]pic=[^&?]*[&?](.+)$/i.exec(url);
        return match ? `?${match[1]}` : '';
    }

    /**
     * Generic method to convert string to boolean value
     * @param input String value that implicitly says if the value is true
     * @returns true or false
     */
    static stringToBoolean(input: string | undefined): boolean {
        if (input === undefined) {
            return false;
        }

        const normalized = input.toLowerCase();
        return normalized !== 'false' && normalized !== 'off' && normalized !== '' && normalized !== '0';
    }

    static jsonToForm(jsonData: any): FormData {
        let formData = new FormData();

        Object.keys(jsonData).forEach((key) => {
            formData.append(key, jsonData[key]);
        });

        return formData;
    }
}
